import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Description as FileIcon,
  CheckCircle as CheckIcon,
  Edit as EditIcon,
  AutoFixHigh as AIIcon,
  Calculate as CalculateIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Save as SaveIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  Settings as SettingsIcon,
  Image as ImageIcon,
  InsertDriveFile as DocIcon
} from '@mui/icons-material';
import { useDropzone } from 'react-dropzone';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import axios from '../utils/axiosConfig';
import AnalisisPUModal from '../components/AnalisisPUModal';
import exportService from '../services/exportService';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Divider from '@mui/material/Divider';
import API_URL from '../config/api';

const steps = ['Información Básica', 'Cargar Catálogo', 'Vista Previa', 'Revisión Final'];

// Utility function to format numbers
const formatNumber = (num) => {
  if (!num || isNaN(num)) return '0.00';
  return parseFloat(num).toLocaleString('es-MX', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

function NuevoPresupuesto() {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [processingConcept, setProcessingConcept] = useState(null);

  // Datos del presupuesto
  const [presupuestoData, setPresupuestoData] = useState({
    nombre_obra: '',
    cliente: '',
    ubicacion: 'Campeche',
    tipo_obra: '',
    fecha_inicio: '',
    fecha_fin: ''
  });

  // Estados para el flujo
  const [uploadedFile, setUploadedFile] = useState(null);
  const [extractedConcepts, setExtractedConcepts] = useState([]);
  const [analyzedConcepts, setAnalyzedConcepts] = useState([]);
  const [processingIndex, setProcessingIndex] = useState(-1);
  const [isProcessingAI, setIsProcessingAI] = useState(false);
  const [modalAnalisisPU, setModalAnalisisPU] = useState(false);
  const [conceptoSeleccionado, setConceptoSeleccionado] = useState(null);
  const [savedPresupuestoId, setSavedPresupuestoId] = useState(null);
  const [showPromptConfig, setShowPromptConfig] = useState(false);
  const [customPrompt, setCustomPrompt] = useState('');
  const [selectedModel, setSelectedModel] = useState('gpt-5-mini');

  // Estados para menús de exportación con plantillas
  const [excelMenuAnchor, setExcelMenuAnchor] = useState(null);
  const [pdfMenuAnchor, setPdfMenuAnchor] = useState(null);

  // Porcentajes
  const [porcentajes, setPorcentajes] = useState({
    indirectos: 17,
    financiamiento: 2,
    utilidad: 10,
    iva: 16
  });

  // Estado de procesamiento
  const [aiProcessingStatus, setAiProcessingStatus] = useState({
    total: 0,
    processed: 0,
    current: '',
    errors: []
  });

  // Cargar configuración inicial
  React.useEffect(() => {
    const savedConfig = localStorage.getItem('appConfig');
    if (savedConfig) {
      try {
        const config = JSON.parse(savedConfig);
        setPorcentajes({
          indirectos: parseFloat(config.porcentaje_indirectos) || 0,
          financiamiento: parseFloat(config.porcentaje_financiamiento) || 0,
          utilidad: parseFloat(config.porcentaje_utilidad) || 0,
          iva: parseFloat(config.iva) || 16
        });
      } catch (error) {
        console.error('Error cargando configuración:', error);
      }
    }

    // Cargar configuración de prompts
    loadPromptConfig();
  }, []);

  const loadPromptConfig = async () => {
    try {
      const response = await axios.get(`${API_URL}/analisis-archivo/prompt-config`);
      setCustomPrompt(response.data.archivo_analisis);
    } catch (error) {
      console.error('Error cargando configuración de prompts:', error);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'text/csv': ['.csv'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
    },
    maxFiles: 1,
    onDrop: async (acceptedFiles) => {
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        setUploadedFile(file);
        await analyzeFile(file);
      }
    }
  });

  // Analizar archivo con IA
  const analyzeFile = async (file) => {
    setLoading(true);
    const formData = new FormData();
    formData.append('file', file);

    if (customPrompt) {
      formData.append('customPrompt', customPrompt);
    }

    try {
      const response = await axios.post(`${API_URL}/analisis-archivo/analyze`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success && response.data.data.conceptos) {
        setExtractedConcepts(response.data.data.conceptos);
        toast.success(`Se extrajeron ${response.data.data.conceptos.length} conceptos del archivo`);

        // Mostrar resumen del análisis
        if (response.data.data.resumen) {
          toast(response.data.data.resumen, {
            duration: 5000,
            icon: 'ℹ️'
          });
        }

        // Avanzar a vista previa
        setActiveStep(2);
      } else {
        toast.error('No se pudieron extraer conceptos del archivo');
      }
    } catch (error) {
      toast.error('Error al analizar el archivo: ' + (error.response?.data?.details || error.message));
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Procesar conceptos con IA uno por uno
  const processConceptsOneByOne = async () => {
    setIsProcessingAI(true);
    setProcessingIndex(0);

    const conceptosActualizados = [...analyzedConcepts];

    for (let i = 0; i < conceptosActualizados.length; i++) {
      setProcessingIndex(i);

      // Si ya tiene precio, saltar
      if (conceptosActualizados[i].precio_unitario > 0) continue;

      try {
        // Procesar solo este concepto
        const response = await axios.post(`${API_URL}/analisis-archivo/process-concepts`, {
          conceptos: [conceptosActualizados[i]],
          configuracion: {
            model: 'gpt-5-mini',
            prompt: customPrompt
          }
        });

        if (response.data.success && response.data.resultados.length > 0) {
          const resultado = response.data.resultados[0];
          conceptosActualizados[i] = {
            ...conceptosActualizados[i],
            precio_unitario: resultado.precio_unitario || 0,
            analisis: resultado.analisis,
            status: resultado.status
          };

          // Actualizar estado después de cada concepto procesado
          setAnalyzedConcepts([...conceptosActualizados]);
        }
      } catch (error) {
        console.error(`Error procesando concepto ${i}:`, error);
        conceptosActualizados[i].status = 'error';
        conceptosActualizados[i].error = error.message;
      }

      // Pequeña pausa para no saturar
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    setIsProcessingAI(false);
    setProcessingIndex(-1);
    toast.success('Análisis de precios unitarios completado');
  };

  // Guardar y continuar al editor
  const saveWithoutProcessing = async () => {
    setLoading(true);
    try {
      // Convertir conceptos extraídos a formato con PU en 0
      const conceptosEnBlanco = extractedConcepts.map(c => ({
        ...c,
        precio_unitario: 0,
        importe: 0
      }));

      // Preparar datos del presupuesto
      const presupuestoPayload = {
        ...presupuestoData,
        costo_directo: 0,
        porcentaje_indirectos: porcentajes.indirectos,
        porcentaje_utilidad: porcentajes.utilidad,
        porcentaje_financiamiento: porcentajes.financiamiento,
        iva: porcentajes.iva,
        importe_total: 0,
        conceptos: conceptosEnBlanco
      };

      // Guardar presupuesto
      const response = await axios.post(`${API_URL}/presupuestos`, presupuestoPayload);

      if (response.data.id) {
        toast.success('Presupuesto guardado exitosamente');
        // Redirigir al editor
        navigate(`/presupuesto/${response.data.id}`);
      }
    } catch (error) {
      console.error('Error guardando presupuesto:', error);
      toast.error('Error al guardar el presupuesto');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (activeStep === 0) {
      if (!presupuestoData.nombre_obra || !presupuestoData.cliente) {
        toast.error('Complete los campos obligatorios');
        return;
      }
    }
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleSavePresupuesto = async () => {
    setLoading(true);
    try {
      const costoDirecto = analyzedConcepts.reduce((sum, c) =>
        sum + ((c.cantidad || 0) * (c.precio_unitario || 0)), 0
      );

      const conceptosConImporte = analyzedConcepts.map(c => ({
        ...c,
        pu: c.precio_unitario,
        importe: (c.cantidad || 0) * (c.precio_unitario || 0),
        // Incluir el análisis si existe
        analisis: c.analisis || null
      }));

      const response = await axios.post(`${API_URL}/presupuestos`, {
        ...presupuestoData,
        conceptos: conceptosConImporte,
        porcentaje_indirectos: porcentajes.indirectos,
        porcentaje_financiamiento: porcentajes.financiamiento,
        porcentaje_utilidad: porcentajes.utilidad,
        iva: porcentajes.iva,
        costo_directo: costoDirecto
      });

      toast.success('Presupuesto creado exitosamente');
      setSavedPresupuestoId(response.data.id);
    } catch (error) {
      toast.error('Error al guardar el presupuesto');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Nuevas funciones de exportación con plantillas
  const handleExcelExport = (template) => {
    const result = exportService.exportToExcel(presupuestoData, analyzedConcepts, porcentajes, template);

    if (result.success) {
      toast.success(`Presupuesto exportado a Excel: ${result.fileName}`);
    } else {
      toast.error(`Error al exportar: ${result.error}`);
    }

    setExcelMenuAnchor(null);
  };

  const handlePdfExport = (template) => {
    const result = exportService.exportToPDF(presupuestoData, analyzedConcepts, porcentajes, template);

    if (result.success) {
      toast.success(`Presupuesto exportado a PDF: ${result.fileName}`);
    } else {
      toast.error(`Error al exportar: ${result.error}`);
    }

    setPdfMenuAnchor(null);
  };

  // Función legacy para compatibilidad temporal
  const exportToExcel = () => {
    const costoDirecto = analyzedConcepts.reduce((sum, c) =>
      sum + ((c.cantidad || 0) * (c.precio_unitario || 0)), 0
    );
    const indirectos = costoDirecto * (porcentajes.indirectos / 100);
    const financiamiento = costoDirecto * (porcentajes.financiamiento / 100);
    const utilidad = costoDirecto * (porcentajes.utilidad / 100);
    const subtotal = costoDirecto + indirectos + financiamiento + utilidad;
    const iva = subtotal * 0.16;
    const total = subtotal + iva;

    const ws_data = [
      ['PRESUPUESTO DE OBRA'],
      [''],
      ['Obra:', presupuestoData.nombre_obra],
      ['Cliente:', presupuestoData.cliente],
      ['Ubicación:', presupuestoData.ubicacion],
      [''],
      ['Código', 'Descripción', 'Unidad', 'Cantidad', 'P.U.', 'Importe'],
      ...analyzedConcepts.map(c => [
        c.codigo,
        c.descripcion,
        c.unidad,
        c.cantidad,
        c.precio_unitario,
        (c.cantidad || 0) * (c.precio_unitario || 0)
      ]),
      [''],
      ['', '', '', '', 'Costo Directo:', costoDirecto],
      ['', '', '', '', `Indirectos (${porcentajes.indirectos}%):`, indirectos],
      ['', '', '', '', `Financiamiento (${porcentajes.financiamiento}%):`, financiamiento],
      ['', '', '', '', `Utilidad (${porcentajes.utilidad}%):`, utilidad],
      ['', '', '', '', 'Subtotal:', subtotal],
      ['', '', '', '', 'IVA (16%):', iva],
      ['', '', '', '', 'TOTAL:', total]
    ];

    const ws = XLSX.utils.aoa_to_sheet(ws_data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Presupuesto');
    XLSX.writeFile(wb, `presupuesto_${presupuestoData.nombre_obra.replace(/\s+/g, '_')}.xlsx`);
    toast.success('Presupuesto exportado a Excel');
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    const costoDirecto = analyzedConcepts.reduce((sum, c) =>
      sum + ((c.cantidad || 0) * (c.precio_unitario || 0)), 0
    );
    const indirectos = costoDirecto * (porcentajes.indirectos / 100);
    const financiamiento = costoDirecto * (porcentajes.financiamiento / 100);
    const utilidad = costoDirecto * (porcentajes.utilidad / 100);
    const subtotal = costoDirecto + indirectos + financiamiento + utilidad;
    const iva = subtotal * 0.16;
    const total = subtotal + iva;

    // Encabezado
    doc.setFontSize(16);
    doc.text('PRESUPUESTO DE OBRA', 105, 20, { align: 'center' });

    doc.setFontSize(10);
    doc.text(`Obra: ${presupuestoData.nombre_obra}`, 20, 35);
    doc.text(`Cliente: ${presupuestoData.cliente}`, 20, 42);
    doc.text(`Ubicación: ${presupuestoData.ubicacion}`, 20, 49);

    // Tabla de conceptos
    const tableData = analyzedConcepts.map(c => [
      c.codigo || '',
      c.descripcion,
      c.unidad,
      c.cantidad.toString(),
      `$${formatNumber(c.precio_unitario)}`,
      `$${formatNumber((c.cantidad || 0) * (c.precio_unitario || 0))}`
    ]);

    doc.autoTable({
      head: [['Código', 'Descripción', 'Unidad', 'Cantidad', 'P.U.', 'Importe']],
      body: tableData,
      startY: 60,
      theme: 'grid'
    });

    // Totales
    const finalY = doc.lastAutoTable.finalY + 10;
    doc.text(`Costo Directo: $${formatNumber(costoDirecto)}`, 120, finalY);
    doc.text(`Indirectos (${porcentajes.indirectos}%): $${formatNumber(indirectos)}`, 120, finalY + 7);
    doc.text(`Financiamiento (${porcentajes.financiamiento}%): $${formatNumber(financiamiento)}`, 120, finalY + 14);
    doc.text(`Utilidad (${porcentajes.utilidad}%): $${formatNumber(utilidad)}`, 120, finalY + 21);
    doc.text(`Subtotal: $${formatNumber(subtotal)}`, 120, finalY + 28);
    doc.text(`IVA (16%): $${formatNumber(iva)}`, 120, finalY + 35);
    doc.setFontSize(12);
    doc.text(`TOTAL: $${formatNumber(total)}`, 120, finalY + 42);

    doc.save(`presupuesto_${presupuestoData.nombre_obra.replace(/\s+/g, '_')}.pdf`);
    toast.success('Presupuesto exportado a PDF');
  };

  const getFileIcon = (filename) => {
    const ext = filename.split('.').pop().toLowerCase();
    if (['jpg', 'jpeg', 'png', 'gif', 'bmp'].includes(ext)) {
      return <ImageIcon />;
    } else if (['pdf'].includes(ext)) {
      return <PdfIcon />;
    } else if (['xls', 'xlsx', 'csv'].includes(ext)) {
      return <ExcelIcon />;
    } else if (['doc', 'docx'].includes(ext)) {
      return <DocIcon />;
    }
    return <FileIcon />;
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0: // Información Básica
        return (
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Nombre de la Obra"
                value={presupuestoData.nombre_obra}
                onChange={(e) => setPresupuestoData({...presupuestoData, nombre_obra: e.target.value})}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Cliente"
                value={presupuestoData.cliente}
                onChange={(e) => setPresupuestoData({...presupuestoData, cliente: e.target.value})}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Ubicación"
                value={presupuestoData.ubicacion}
                onChange={(e) => setPresupuestoData({...presupuestoData, ubicacion: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Tipo de Obra"
                value={presupuestoData.tipo_obra}
                onChange={(e) => setPresupuestoData({...presupuestoData, tipo_obra: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="date"
                label="Fecha Inicio"
                InputLabelProps={{ shrink: true }}
                value={presupuestoData.fecha_inicio}
                onChange={(e) => setPresupuestoData({...presupuestoData, fecha_inicio: e.target.value})}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                type="date"
                label="Fecha Fin"
                InputLabelProps={{ shrink: true }}
                value={presupuestoData.fecha_fin}
                onChange={(e) => setPresupuestoData({...presupuestoData, fecha_fin: e.target.value})}
              />
            </Grid>
          </Grid>
        );

      case 1: // Cargar Catálogo
        return (
          <Box>
            <Box
              {...getRootProps()}
              sx={{
                border: '2px dashed #ccc',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                cursor: 'pointer',
                backgroundColor: isDragActive ? '#f0f0f0' : 'transparent',
                '&:hover': { backgroundColor: '#f5f5f5' }
              }}
            >
              <input {...getInputProps()} />
              <UploadIcon sx={{ fontSize: 48, color: '#999', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                {isDragActive ? 'Suelte el archivo aquí' : 'Arrastre un archivo o haga clic para seleccionar'}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Formatos soportados: CSV, Excel (.csv, .xls, .xlsx)
              </Typography>
              <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                La IA analizará el contenido y extraerá los conceptos de obra automáticamente
              </Typography>
            </Box>

            {uploadedFile && (
              <Card sx={{ mt: 3 }}>
                <CardContent>
                  <Box display="flex" alignItems="center">
                    {getFileIcon(uploadedFile.name)}
                    <Box flex={1} ml={2}>
                      <Typography variant="subtitle1">{uploadedFile.name}</Typography>
                      <Typography variant="body2" color="textSecondary">
                        {(uploadedFile.size / 1024 / 1024).toFixed(2)} MB
                      </Typography>
                    </Box>
                    {loading ? <CircularProgress size={24} /> : <CheckIcon color="success" />}
                  </Box>
                </CardContent>
              </Card>
            )}

          </Box>
        );

      case 2: // Vista Previa (sin PU)
        return (
          <Box>
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Conceptos extraídos del archivo
              </Typography>
              <Typography variant="body2">
                Se han identificado {extractedConcepts.length} conceptos. Puede proceder a calcular los precios unitarios
                con IA o guardar el presupuesto sin precios para completarlo manualmente.
              </Typography>
            </Alert>

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Total Conceptos
                    </Typography>
                    <Typography variant="h4">
                      {extractedConcepts.length}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Modelo IA Seleccionado
                    </Typography>
                    <Typography variant="h6">
                      {selectedModel}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Estado
                    </Typography>
                    <Chip label="Sin Precios" color="warning" />
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Código</TableCell>
                    <TableCell>Descripción</TableCell>
                    <TableCell>Unidad</TableCell>
                    <TableCell align="right">Cantidad</TableCell>
                    <TableCell align="center">P.U.</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {extractedConcepts.map((concept, index) => (
                    <TableRow key={index}>
                      <TableCell>{concept.codigo || '-'}</TableCell>
                      <TableCell>{concept.descripcion}</TableCell>
                      <TableCell>{concept.unidad || 'PZA'}</TableCell>
                      <TableCell align="right">{concept.cantidad || 1}</TableCell>
                      <TableCell align="center">
                        <Chip label="Sin calcular" size="small" variant="outlined" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <Box mt={3} display="flex" justifyContent="center" gap={2}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                startIcon={<SaveIcon />}
                onClick={() => {
                  // Pasar conceptos sin PU a la revisión final
                  setAnalyzedConcepts(extractedConcepts.map(c => ({...c, precio_unitario: 0})));
                  setActiveStep(3);
                }}
                disabled={loading}
              >
                Continuar a Revisión
              </Button>
            </Box>
          </Box>
        );

      case 3: // Revisión Final
        return (
          <Box>
            <Alert severity="success" sx={{ mb: 3 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    Presupuesto listo para revisión
                  </Typography>
                  <Typography variant="body2">
                    Puede editar los valores directamente en la tabla o calcular automáticamente con IA.
                  </Typography>
                </Box>
                {!isProcessingAI && analyzedConcepts.filter(c => c.precio_unitario === 0).length > 0 && (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<AIIcon />}
                    onClick={processConceptsOneByOne}
                    size="small"
                  >
                    Calcular PU con IA ({analyzedConcepts.filter(c => c.precio_unitario === 0).length} pendientes)
                  </Button>
                )}
              </Box>
            </Alert>

            {isProcessingAI && (
              <Box sx={{ mb: 3 }}>
                <LinearProgress
                  variant="determinate"
                  value={(processingIndex + 1) / analyzedConcepts.length * 100}
                />
                <Typography variant="body2" sx={{ mt: 1 }} align="center">
                  Procesando concepto {processingIndex + 1} de {analyzedConcepts.length}
                  {analyzedConcepts[processingIndex] && (
                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                      {analyzedConcepts[processingIndex].descripcion.substring(0, 80)}...
                    </Typography>
                  )}
                </Typography>
              </Box>
            )}

            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Total Conceptos
                    </Typography>
                    <Typography variant="h4">
                      {analyzedConcepts.length}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Con PU Asignado
                    </Typography>
                    <Typography variant="h4" color="success.main">
                      {analyzedConcepts.filter(c => c.precio_unitario > 0).length}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Con Errores
                    </Typography>
                    <Typography variant="h4" color="error.main">
                      {analyzedConcepts.filter(c => c.status === 'error').length}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Importe Total
                    </Typography>
                    <Typography variant="h5">
                      ${formatNumber(
                        analyzedConcepts.reduce((sum, c) =>
                          sum + ((c.cantidad || 0) * (c.precio_unitario || 0)), 0
                        )
                      )}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Código</TableCell>
                    <TableCell>Descripción</TableCell>
                    <TableCell>Unidad</TableCell>
                    <TableCell align="right">Cantidad</TableCell>
                    <TableCell align="right">P.U.</TableCell>
                    <TableCell align="right">Importe</TableCell>
                    <TableCell>Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {analyzedConcepts.map((concept, index) => (
                    <TableRow key={index}
                      sx={{
                        backgroundColor: concept.status === 'error' ? '#ffebee' :
                                       processingIndex === index ? '#e3f2fd' : 'inherit',
                        animation: processingIndex === index ? 'pulse 1.5s infinite' : 'none'
                      }}>
                      <TableCell>{concept.codigo || '-'}</TableCell>
                      <TableCell>
                        <TextField
                          value={concept.descripcion}
                          onChange={(e) => {
                            const newConcepts = [...analyzedConcepts];
                            newConcepts[index].descripcion = e.target.value;
                            setAnalyzedConcepts(newConcepts);
                          }}
                          variant="standard"
                          fullWidth
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          value={concept.unidad}
                          onChange={(e) => {
                            const newConcepts = [...analyzedConcepts];
                            newConcepts[index].unidad = e.target.value;
                            setAnalyzedConcepts(newConcepts);
                          }}
                          variant="standard"
                          size="small"
                          sx={{ width: 60 }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <TextField
                          type="number"
                          value={concept.cantidad}
                          onChange={(e) => {
                            const newConcepts = [...analyzedConcepts];
                            newConcepts[index].cantidad = parseFloat(e.target.value) || 0;
                            setAnalyzedConcepts(newConcepts);
                          }}
                          variant="standard"
                          size="small"
                          sx={{ width: 80 }}
                          inputProps={{ style: { textAlign: 'right' } }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Box display="flex" alignItems="center">
                          {processingIndex === index ? (
                            <Box display="flex" alignItems="center">
                              <CircularProgress size={20} sx={{ mr: 1 }} />
                              <Typography variant="body2" color="primary">Calculando...</Typography>
                            </Box>
                          ) : (
                            <>
                              <Typography>${formatNumber(concept.precio_unitario)}</Typography>
                              {concept.status === 'error' && (
                                <Tooltip title={concept.error}>
                                  <Chip label="Error" color="error" size="small" sx={{ ml: 1 }} />
                                </Tooltip>
                              )}
                            </>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        ${formatNumber((concept.cantidad || 0) * (concept.precio_unitario || 0))}
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Ver/Editar Análisis de PU">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => {
                              setConceptoSeleccionado({...concept, index});
                              setModalAnalisisPU(true);
                            }}
                          >
                            <CalculateIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Eliminar">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              const newConcepts = analyzedConcepts.filter((_, i) => i !== index);
                              setAnalyzedConcepts(newConcepts);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}

                  {/* Totales */}
                  <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                    <TableCell colSpan={5} align="right">
                      <Typography variant="subtitle1" fontWeight="bold">
                        Costo Directo:
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="subtitle1" fontWeight="bold">
                        ${formatNumber(analyzedConcepts.reduce((sum, c) =>
                          sum + ((c.cantidad || 0) * (c.precio_unitario || 0)), 0
                        ))}
                      </Typography>
                    </TableCell>
                    <TableCell />
                  </TableRow>

                  {/* Porcentajes */}
                  <TableRow>
                    <TableCell colSpan={4} align="right">Indirectos:</TableCell>
                    <TableCell align="right">
                      <TextField
                        type="number"
                        value={porcentajes.indirectos}
                        onChange={(e) => setPorcentajes({...porcentajes, indirectos: parseFloat(e.target.value) || 0})}
                        variant="standard"
                        size="small"
                        sx={{ width: 60 }}
                        InputProps={{ endAdornment: '%' }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      ${formatNumber(analyzedConcepts.reduce((sum, c) =>
                        sum + ((c.cantidad || 0) * (c.precio_unitario || 0)), 0
                      ) * (porcentajes.indirectos / 100))}
                    </TableCell>
                    <TableCell />
                  </TableRow>

                  <TableRow>
                    <TableCell colSpan={4} align="right">Financiamiento:</TableCell>
                    <TableCell align="right">
                      <TextField
                        type="number"
                        value={porcentajes.financiamiento}
                        onChange={(e) => setPorcentajes({...porcentajes, financiamiento: parseFloat(e.target.value) || 0})}
                        variant="standard"
                        size="small"
                        sx={{ width: 60 }}
                        InputProps={{ endAdornment: '%' }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      ${formatNumber(analyzedConcepts.reduce((sum, c) =>
                        sum + ((c.cantidad || 0) * (c.precio_unitario || 0)), 0
                      ) * (porcentajes.financiamiento / 100))}
                    </TableCell>
                    <TableCell />
                  </TableRow>

                  <TableRow>
                    <TableCell colSpan={4} align="right">Utilidad:</TableCell>
                    <TableCell align="right">
                      <TextField
                        type="number"
                        value={porcentajes.utilidad}
                        onChange={(e) => setPorcentajes({...porcentajes, utilidad: parseFloat(e.target.value) || 0})}
                        variant="standard"
                        size="small"
                        sx={{ width: 60 }}
                        InputProps={{ endAdornment: '%' }}
                      />
                    </TableCell>
                    <TableCell align="right">
                      ${formatNumber(analyzedConcepts.reduce((sum, c) =>
                        sum + ((c.cantidad || 0) * (c.precio_unitario || 0)), 0
                      ) * (porcentajes.utilidad / 100))}
                    </TableCell>
                    <TableCell />
                  </TableRow>

                  {/* Total Final */}
                  <TableRow sx={{ backgroundColor: '#e8f5e9' }}>
                    <TableCell colSpan={5} align="right">
                      <Typography variant="h6" fontWeight="bold">TOTAL (con IVA):</Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="h6" fontWeight="bold" color="primary">
                        ${formatNumber((() => {
                          const costoDirecto = analyzedConcepts.reduce((sum, c) =>
                            sum + ((c.cantidad || 0) * (c.precio_unitario || 0)), 0
                          );
                          const subtotal = costoDirecto * (1 + (porcentajes.indirectos + porcentajes.financiamiento + porcentajes.utilidad) / 100);
                          return subtotal * 1.16;
                        })())}
                      </Typography>
                    </TableCell>
                    <TableCell />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Nuevo Presupuesto con IA
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Stepper activeStep={activeStep}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      <Paper sx={{ p: 3 }}>
        {renderStepContent()}

        <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
          <Button
            disabled={activeStep === 0}
            onClick={handleBack}
          >
            Anterior
          </Button>

          {activeStep === steps.length - 1 ? (
            <Box display="flex" gap={1}>
              {savedPresupuestoId && (
                <>
                  <Button
                    variant="outlined"
                    startIcon={<ExcelIcon />}
                    onClick={(e) => setExcelMenuAnchor(e.currentTarget)}
                    color="success"
                  >
                    Exportar Excel
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<PdfIcon />}
                    onClick={(e) => setPdfMenuAnchor(e.currentTarget)}
                    color="error"
                  >
                    Exportar PDF
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => navigate(`/presupuesto/${savedPresupuestoId}`)}
                  >
                    Ir al Editor
                  </Button>
                </>
              )}
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSavePresupuesto}
                disabled={loading || savedPresupuestoId}
              >
                {savedPresupuestoId ? 'Guardado' : 'Guardar Presupuesto'}
              </Button>
            </Box>
          ) : ( // Mostrar siguiente en todos los pasos excepto el último
            <Button
              variant="contained"
              onClick={handleNext}
              disabled={loading || (activeStep === 1 && extractedConcepts.length === 0)}
            >
              Siguiente
            </Button>
          )}
        </Box>
      </Paper>

      {/* Modal de configuración de prompts */}
      <Dialog
        open={showPromptConfig}
        onClose={() => setShowPromptConfig(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Configuración de Análisis con IA</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              select
              fullWidth
              label="Modelo de IA"
              value={selectedModel}
              onChange={(e) => setSelectedModel(e.target.value)}
              SelectProps={{
                native: true,
              }}
              sx={{ mb: 2 }}
            >
              <option value="gpt-5-mini">GPT-5 Mini (Recomendado)</option>
              <option value="gpt-4-turbo-preview">GPT-4 Turbo</option>
              <option value="gpt-4">GPT-4</option>
              <option value="gpt-3.5-turbo">GPT-3.5 Turbo</option>
            </TextField>

            <TextField
              fullWidth
              multiline
              rows={10}
              label="Prompt personalizado (opcional)"
              value={customPrompt}
              onChange={(e) => setCustomPrompt(e.target.value)}
              helperText="Personalice las instrucciones para el análisis de archivos y generación de PU"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPromptConfig(false)}>Cancelar</Button>
          <Button onClick={() => setShowPromptConfig(false)} variant="contained">
            Guardar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de análisis PU */}
      <AnalisisPUModal
        open={modalAnalisisPU}
        onClose={() => {
          setModalAnalisisPU(false);
          setConceptoSeleccionado(null);
        }}
        concept={conceptoSeleccionado}
        onSave={(nuevoPrecio, nuevoAnalisis) => {
          if (conceptoSeleccionado) {
            const newConcepts = [...analyzedConcepts];
            newConcepts[conceptoSeleccionado.index].precio_unitario = nuevoPrecio;
            // Guardar el análisis actualizado
            if (nuevoAnalisis) {
              newConcepts[conceptoSeleccionado.index].analisis = nuevoAnalisis;
            }
            setAnalyzedConcepts(newConcepts);
            toast.success('Precio unitario actualizado');
          }
        }}
      />

      {/* Menú de plantillas de Excel */}
      <Menu
        anchorEl={excelMenuAnchor}
        open={Boolean(excelMenuAnchor)}
        onClose={() => setExcelMenuAnchor(null)}
      >
        <MenuItem onClick={() => handleExcelExport('standard')}>
          📊 Estándar
        </MenuItem>
        <MenuItem onClick={() => handleExcelExport('detallada')}>
          📋 Detallada con Análisis
        </MenuItem>
        <MenuItem onClick={() => handleExcelExport('simplificada')}>
          📄 Simplificada
        </MenuItem>
        <MenuItem onClick={() => handleExcelExport('corporativa')}>
          🏢 Corporativa
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => {
          setExcelMenuAnchor(null);
          navigate('/configuracion-plantillas');
        }}>
          <SettingsIcon fontSize="small" sx={{ mr: 1 }} />
          Configurar Plantillas
        </MenuItem>
      </Menu>

      {/* Menú de plantillas de PDF */}
      <Menu
        anchorEl={pdfMenuAnchor}
        open={Boolean(pdfMenuAnchor)}
        onClose={() => setPdfMenuAnchor(null)}
      >
        <MenuItem onClick={() => handlePdfExport('standard')}>
          📄 Estándar
        </MenuItem>
        <MenuItem onClick={() => handlePdfExport('detallada')}>
          📊 Detallada
        </MenuItem>
        <MenuItem onClick={() => handlePdfExport('ejecutiva')}>
          💼 Ejecutiva
        </MenuItem>
        <MenuItem onClick={() => handlePdfExport('minimalista')}>
          ⬜ Minimalista
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => {
          setPdfMenuAnchor(null);
          navigate('/configuracion-plantillas');
        }}>
          <SettingsIcon fontSize="small" sx={{ mr: 1 }} />
          Configurar Plantillas
        </MenuItem>
      </Menu>
    </Box>
  );
}

export default NuevoPresupuesto;